import { useState, useEffect } from 'react';
import { get, post, put, remove, API_URL } from '../../utils/api';
import { alphabeticalSort } from '../../utils/sortUtils';

const SiteList = ({ onRefresh, onSiteSelect }) => {
  const [sites, setSites] = useState([]);
  const [displayedSites, setDisplayedSites] = useState([]);
  const [municipalities, setMunicipalities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentSite, setCurrentSite] = useState(null);
  const [formMode, setFormMode] = useState('create'); // 'create' or 'edit'
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState({
    key: 'name',
    direction: 'ascending'
  });
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    municipalityUuid: '',
    status: 'PENDING',
    bulkSupplier: 'UNKNOWN',
    peakStartTime: '',
    peakEndTime: '',
    googleEarthUrl: '',
    googleStreetViewUrl: ''
  });

  // Load sites and municipalities on component mount
  useEffect(() => {
    fetchSites();
    fetchMunicipalities();
  }, []);
  
  // Apply sorting whenever sites or sort config changes
  useEffect(() => {
    if (sites.length > 0) {
      const sortedSites = sortSites(sites);
      setDisplayedSites(sortedSites);
    }
  }, [sites, sortConfig]);

  // Fetch sites from the API
  const fetchSites = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await get(`${API_URL}/site/sites`);
      setSites(response.data);
    } catch (err) {
      // console.error('Error fetching sites:', err);
      setError('Failed to load sites. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Sort sites based on current config
  const sortSites = (sitesToSort) => {
    return alphabeticalSort(
      sitesToSort, 
      sortConfig.key === 'municipality' 
        ? (site) => getMunicipalityName(site.municipalityUuid)
        : sortConfig.key, 
      sortConfig.direction === 'ascending'
    );
  };
  
  // Handle column header click for sorting
  const handleSort = (key) => {
    setSortConfig((prevConfig) => {
      // If clicking the same column, toggle direction
      if (prevConfig.key === key) {
        return {
          key,
          direction: prevConfig.direction === 'ascending' ? 'descending' : 'ascending'
        };
      }
      
      // If clicking a new column, sort ascending by default
      return {
        key,
        direction: 'ascending'
      };
    });
  };
  
  // Get sort indicator arrow
  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'ascending' ? '↑' : '↓';
  };

  // Fetch municipalities from the API (for the dropdown)
  const fetchMunicipalities = async () => {
    try {
      const response = await get(`${API_URL}/site/municipalities`);
      setMunicipalities(response.data);
    } catch (err) {
      // console.error('Error fetching municipalities:', err);
      // Don't set error here to avoid disrupting the main sites display
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Open modal for creating a new site
  const handleCreate = () => {
    setFormData({
      name: '',
      code: '',
      description: '',
      municipalityUuid: '',
      status: 'PENDING',
      bulkSupplier: 'UNKNOWN',
      peakStartTime: '',
      peakEndTime: '',
      googleEarthUrl: '',
      googleStreetViewUrl: ''
    });
    setFormMode('create');
    setCurrentSite(null);
    setIsModalOpen(true);
  };

  // Open modal for editing an existing site
  const handleEdit = (site) => {
    setFormData({
      name: site.name,
      code: site.code,
      description: site.description || '',
      municipalityUuid: site.municipalityUuid,
      status: site.status,
      bulkSupplier: site.bulkSupplier,
      peakStartTime: site.peakStartTime || '',
      peakEndTime: site.peakEndTime || '',
      googleEarthUrl: site.googleEarthUrl || '',
      googleStreetViewUrl: site.googleStreetViewUrl || ''
    });
    setFormMode('edit');
    setCurrentSite(site);
    setIsModalOpen(true);
  };

  // Submit form to create or update a site
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Format the data
      const data = {
        name: formData.name,
        code: formData.code,
        description: formData.description || null,
        municipalityUuid: formData.municipalityUuid,
        status: formData.status,
        bulkSupplier: formData.bulkSupplier,
        peakStartTime: formData.peakStartTime || null,
        peakEndTime: formData.peakEndTime || null,
        googleEarthUrl: formData.googleEarthUrl || null,
        googleStreetViewUrl: formData.googleStreetViewUrl || null
      };
      
      if (formMode === 'edit' && currentSite) {
        // Add UUID for updates
        data.uuid = currentSite.uuid;
        
        // Update existing site using the API utility with CSRF token
        await put(`${API_URL}/site/sites`, data);
      } else {
        // Create new site using the API utility with CSRF token
        await post(`${API_URL}/site/sites`, data);
      }
      
      // Close modal and refresh data
      setIsModalOpen(false);
      fetchSites();
      if (onRefresh) onRefresh();
    } catch (err) {
      // console.error('Error saving site:', err);
      setError('Failed to save site. ' + (err.response?.data?.message || 'Please try again later.'));
    }
  };

  // Handle site deletion
  const handleDelete = async (site) => {
    if (!confirm(`Are you sure you want to delete ${site.name}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      // Use the API utility with CSRF token
      await remove(`${API_URL}/site/sites/${site.uuid}`);
      
      // Refresh data
      fetchSites();
      if (onRefresh) onRefresh();
    } catch (err) {
      // console.error('Error deleting site:', err);
      alert('Failed to delete site. It may have associated units that need to be deleted first.');
    }
  };

  // Get municipality name by UUID
  const getMunicipalityName = (uuid) => {
    const municipality = municipalities.find(m => m.uuid === uuid);
    return municipality ? municipality.name : 'Unknown';
  };

  // Format time for display
  const formatTime = (timeString) => {
    if (!timeString) return '-';
    return timeString;
  };

  // Handle manage units button click
  const handleManageUnits = (site) => {
    if (onSiteSelect) {
      onSiteSelect(site);
    }
  };

  if (loading && sites.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Sites</h2>
        <button
          className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded"
          onClick={handleCreate}
        >
          Add Site
        </button>
      </div>
      
      {/* Sites Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-gray-200">
          <thead className="bg-gray-100">
            <tr>
              <th 
                className="py-2 px-4 border-b cursor-pointer hover:bg-gray-200"
                onClick={() => handleSort('name')}
              >
                Name {getSortIndicator('name')}
              </th>
              <th 
                className="py-2 px-4 border-b cursor-pointer hover:bg-gray-200"
                onClick={() => handleSort('code')}
              >
                Code {getSortIndicator('code')}
              </th>
              <th 
                className="py-2 px-4 border-b cursor-pointer hover:bg-gray-200"
                onClick={() => handleSort('municipality')}
              >
                Municipality {getSortIndicator('municipality')}
              </th>
              <th 
                className="py-2 px-4 border-b cursor-pointer hover:bg-gray-200"
                onClick={() => handleSort('status')}
              >
                Status {getSortIndicator('status')}
              </th>
              <th 
                className="py-2 px-4 border-b cursor-pointer hover:bg-gray-200"
                onClick={() => handleSort('bulkSupplier')}
              >
                Bulk Supplier {getSortIndicator('bulkSupplier')}
              </th>
              <th className="py-2 px-4 border-b">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && sites.length === 0 ? (
              <tr>
                <td colSpan="6" className="py-4 text-center">
                  <div className="flex justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
                  </div>
                </td>
              </tr>
            ) : displayedSites.length === 0 ? (
              <tr>
                <td colSpan="6" className="py-4 text-center">No sites found</td>
              </tr>
            ) : (
              displayedSites.map((site) => (
                <tr key={site.uuid} className="hover:bg-gray-50">
                  <td className="py-2 px-4">{site.name}</td>
                  <td className="py-2 px-4">{site.code}</td>
                  <td className="py-2 px-4">{getMunicipalityName(site.municipalityUuid)}</td>
                  <td className="py-2 px-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      site.status === 'ACTIVE' ? 'bg-green-100 text-green-800' :
                      site.status === 'INACTIVE' ? 'bg-red-100 text-red-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {site.status}
                    </span>
                  </td>
                  <td className="py-2 px-4">{site.bulkSupplier}</td>
                  <td className="py-2 px-4">
                    <div className="flex space-x-2">
                      <button
                        className="text-blue-500 hover:text-blue-700"
                        onClick={() => handleEdit(site)}
                      >
                        Edit
                      </button>
                      <button
                        className="text-red-500 hover:text-red-700"
                        onClick={() => handleDelete(site)}
                      >
                        Delete
                      </button>
                      <button
                        className="text-purple-500 hover:text-purple-700"
                        onClick={() => handleManageUnits(site)}
                      >
                        Manage Units
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {/* Site Form Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-2xl w-full max-h-screen overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4">
              {formMode === 'create' ? 'Add Site' : 'Edit Site'}
            </h2>
            
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                    required
                    maxLength={128}
                  />
                  <p className="text-xs text-gray-500 mt-1">Unique identifier for the site</p>
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                    rows="3"
                  ></textarea>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Municipality <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="municipalityUuid"
                    value={formData.municipalityUuid}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                    required
                  >
                    <option value="">Select municipality</option>
                    {municipalities.map(municipality => (
                      <option key={municipality.uuid} value={municipality.uuid}>
                        {municipality.name} ({municipality.code})
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                    required
                  >
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                    <option value="PENDING">Pending</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bulk Supplier <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="bulkSupplier"
                    value={formData.bulkSupplier}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                    required
                  >
                    <option value="ESKOM">Eskom</option>
                    <option value="MUNICIPALITY">Municipality</option>
                    <option value="OTHER">Other</option>
                    <option value="UNKNOWN">Unknown</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Peak Start Time
                  </label>
                  <input
                    type="time"
                    name="peakStartTime"
                    value={formData.peakStartTime}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Peak End Time
                  </label>
                  <input
                    type="time"
                    name="peakEndTime"
                    value={formData.peakEndTime}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Google Earth URL
                  </label>
                  <input
                    type="url"
                    name="googleEarthUrl"
                    value={formData.googleEarthUrl}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                    placeholder="https://earth.google.com/web/..."
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Google Street View URL
                  </label>
                  <input
                    type="url"
                    name="googleStreetViewUrl"
                    value={formData.googleStreetViewUrl}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                    placeholder="https://www.google.com/maps/@..."
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100"
                  onClick={() => setIsModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  {formMode === 'create' ? 'Create Site' : 'Update Site'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SiteList; 